import {
  DatePicker,
  Form,
  InputNumber,
  Modal,
  Radio,
  Select,
  ShowInput,
  Tooltip,
  TreeSelect,
  useAsync,
} from '@maxtropy/components';
import { Cascader, Col, Row, Spin } from 'antd';
import React, { FC, useEffect, useMemo, useState } from 'react';
import styles from './index.module.scss';
import { useRequest } from 'ahooks';
import {
  apiV2ProductionCostAddConfigPost,
  apiV2ProductionCostGetConfigDetailPost,
  apiV2ProductionCostGetDeviceListPost,
  apiV2ProductionCostGetEnergyUnitTreePost,
  apiV2ProductionCostGetProductionListPost,
  apiV2ProductionCostUpdateConfigPost,
} from '@maxtropy/device-customer-apis-v2';
import { convertToTreeSelect } from '../utils';
import { formatOptionData, getChildNodesByParentIds } from '@/shared/components/CascadingMultipleSelector/utils';
import { DefaultOptionType } from 'antd/es/cascader';
import { getDeviceTypeTree } from '@/api/deviceType';
import { getCrossDataProperties } from '@/shared/api/options';
import dayjs from 'dayjs';
import { convertDataToEntities, formatToDataNode, getDeviceType } from '@/shared/utils/cascade-util';
import _ from 'lodash';
import { InfoCircleOutlined } from '@ant-design/icons';

export interface ICreateProductionConfigModal {
  id?: number;
  onConfirm?: () => void;
  onCancel?: () => void;
  openCreateModal: boolean;
}

const CreateProductionConfigModal: FC<ICreateProductionConfigModal> = props => {
  const { onCancel, onConfirm, id, openCreateModal } = props;
  const [form] = Form.useForm();
  const [confirmLoading, setConfirmLoading] = useState(false);
  const deviceTypeId: number[] = Form.useWatch('deviceTypeId', form); // 设备类型id
  const deviceId: number = Form.useWatch('deviceId', form); // 设备类型id
  const productionId: number = Form.useWatch('productionId', form); // 产品id
  const hasEnd: boolean = Form.useWatch('hasEnd', form); // 是否有结束时间

  const deviceTypeData = useAsync(getDeviceTypeTree);

  // 编辑查询详情
  const { loading, data: detail } = useRequest(
    async () => {
      return apiV2ProductionCostGetConfigDetailPost({
        id,
      });
    },
    {
      ready: !!id && openCreateModal,
      refreshDeps: [id, openCreateModal],
    }
  );

  // 赋值
  useEffect(() => {
    if (!detail || !openCreateModal || !id) return;
    // const tree: any[] = formatToDataNode(deviceTypeData?.tree as any);
    // const entities = convertDataToEntities(tree);
    // const deviceType = getDeviceType(entities, detail.deviceTypeId!);
    form.setFieldsValue({
      ...detail,
      // deviceTypeId: deviceType,
      startTime: dayjs(detail.startTime),
      endTime: detail.hasEnd ? dayjs(detail.endTime) : undefined,
    });
  }, [id, detail, form, openCreateModal]);

  // 获取所有的用能单元属性结构
  const { data: unitTreelist } = useRequest(
    () =>
      apiV2ProductionCostGetEnergyUnitTreePost({}).then(res => {
        return res.list;
      }),
    {
      ready: !!openCreateModal && !id,
      refreshDeps: [openCreateModal, id],
    }
  );

  const unitTreeData = useMemo(() => {
    return convertToTreeSelect(unitTreelist);
  }, [unitTreelist]);

  // 获取所有产品列表
  const { data: productionlist } = useRequest(
    () =>
      apiV2ProductionCostGetProductionListPost({ flag: true }).then(res => {
        return res.list;
      }),
    {
      ready: !!openCreateModal,
      refreshDeps: [openCreateModal],
    }
  );

  // 根据设备类目查询设备
  const { data: devicelist } = useRequest(
    async () => {
      const res = await apiV2ProductionCostGetDeviceListPost({ id: deviceTypeId[deviceTypeId.length - 1] });
      return res.list;
    },
    {
      ready: !!deviceTypeId && openCreateModal && !id,
      refreshDeps: [deviceTypeId, openCreateModal, id],
    }
  );

  // 根据设备查询数据属性
  const { data: devicePropertylist } = useRequest(
    () => {
      return getCrossDataProperties({ id: deviceId.toString() });
    },
    {
      ready: !!deviceId && openCreateModal && !id,
      refreshDeps: [deviceId, openCreateModal, id],
    }
  );

  // 点击确定
  const onOk = () => {
    form.validateFields().then(values => {
      setConfirmLoading(true);
      const api = id ? apiV2ProductionCostUpdateConfigPost : apiV2ProductionCostAddConfigPost;
      api({
        ...values,
        id,
        energyUnitId: id ? detail?.energyUnitId : values.energyUnitId[values.energyUnitId.length - 1],
        deviceTypeId: id ? detail?.deviceTypeId : values.deviceTypeId[values.deviceTypeId.length - 1],
        productionId: id ? detail?.productionId : values.productionId,
        deviceId: id ? detail?.deviceId : values.deviceId,
        dataPropertyId: id ? detail?.dataPropertyId : values.dataPropertyId,
        startTime: dayjs(values.startTime).valueOf(),
        endTime: values.hasEnd ? dayjs(values.endTime).valueOf() : undefined,
      })
        .then(() => {
          onConfirm?.();
          form.resetFields();
        })
        .finally(() => setConfirmLoading(false));
    });
  };

  const filter = (inputValue: string, path: DefaultOptionType[]) =>
    path.some(option => (option.label as string).toLowerCase().indexOf(inputValue.toLowerCase()) > -1);

  return (
    <Modal
      title={`${id ? '编辑' : '新增'}产量配置`}
      size="large"
      contentClassName="modal-form-content"
      open={openCreateModal}
      bodyScroll
      onOk={() => {
        onOk();
      }}
      confirmLoading={confirmLoading}
      onCancel={() => {
        onCancel?.();
        form.resetFields();
      }}
    >
      <Spin spinning={id ? loading : false}>
        <Form form={form} initialValues={{ hasEnd: false }}>
          <Form.Item
            label="选择单元"
            name={id ? 'energyUnitName' : 'energyUnitId'}
            rules={[{ required: true, message: '请选择' }]}
          >
            {id ? (
              <ShowInput />
            ) : (
              <Cascader
                disabled={!!id}
                options={unitTreeData}
                allowClear={false}
                showSearch={{ filter }}
                placeholder={'请选择单元'}
              />
            )}
          </Form.Item>

          <Form.Item
            label="选择产品"
            name={id ? 'productionName' : 'productionId'}
            rules={[{ required: true, message: '请选择' }]}
          >
            {id ? (
              <ShowInput />
            ) : (
              <Select
                disabled={!!id}
                placeholder="请选择"
                options={(productionlist ?? []).map(i => ({
                  label: (i.name ?? '') + '-' + (i.unitName ?? '--'),
                  value: i.id,
                }))}
              />
            )}
          </Form.Item>
          <Form.Item label={<span className={styles.required}>产品来源</span>} style={{ marginBottom: 0 }}>
            <Form.Item
              name={id ? 'deviceTypeName' : 'deviceTypeId'}
              rules={[{ required: true, message: '请选择所属类目' }]}
              style={{ display: 'inline-block', width: 'calc(33% - 10px)', marginRight: 10 }}
            >
              {id ? (
                <ShowInput />
              ) : (
                <Cascader
                  disabled={!!id}
                  options={formatOptionData(deviceTypeData)}
                  allowClear={false}
                  fieldNames={{ children: 'child' }}
                  showSearch={{ filter }}
                  placeholder={'请选择所属类目'}
                  onChange={() => {
                    form.setFieldsValue({
                      deviceId: undefined,
                      dataPropertyId: undefined,
                    });
                  }}
                />
              )}
            </Form.Item>
            <Form.Item
              name={id ? 'deviceName' : 'deviceId'}
              rules={[{ required: true, message: '请选择设备' }]}
              style={{ display: 'inline-block', width: 'calc(33% - 10px)', marginRight: 10 }}
            >
              {id ? (
                <ShowInput />
              ) : (
                <Select
                  disabled={!!id}
                  placeholder="请选择设备"
                  options={(devicelist ?? []).map(i => ({
                    label: i.name,
                    value: i.id,
                  }))}
                  showSearch
                  optionFilterProp="label"
                  onChange={() => form.setFieldValue('dataPropertyId', undefined)}
                />
              )}
            </Form.Item>
            <Form.Item
              name={id ? 'dataPropertyName' : 'dataPropertyId'}
              rules={[{ required: true, message: '请选择数据属性' }]}
              style={{ display: 'inline-block', width: 'calc(33% - 10px)' }}
            >
              {id ? (
                <ShowInput />
              ) : (
                <Select
                  disabled={!!id}
                  placeholder="请选择数据属性"
                  options={_.uniqBy(
                    (devicePropertylist ?? []).map(i => ({
                      label: i.datapropertyName,
                      value: i.datapropertyId,
                    })),
                    'value'
                  )}
                  showSearch
                  optionFilterProp="label"
                />
              )}
            </Form.Item>
          </Form.Item>
          <Form.Item
            label={
              <>
                转化系数
                <Tooltip title="例如转换系数为10，则展示产品=选择产品产量/10">
                  <InfoCircleOutlined style={{ color: 'var(--warning-color)', paddingLeft: 5 }} />
                </Tooltip>
              </>
            }
            name="transferRate"
            rules={[{ required: true, message: '请输入' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              addonAfter={productionlist?.find(i => i.id === productionId)?.unitName}
              precision={6}
              min={0.0}
              max={99999999.999999}
            />
          </Form.Item>
          <Form.Item label="展示产品" name="showProductionId" rules={[{ required: true, message: '请选择' }]}>
            <Select
              allowClear
              placeholder="请选择"
              options={(productionlist ?? []).map(i => ({
                label: (i.name ?? '') + '-' + (i.unitName ?? ''),
                value: i.id,
              }))}
            />
          </Form.Item>
          <Form.Item label="开始生效时间" name="startTime" rules={[{ required: true, message: '请选择' }]}>
            <DatePicker showTime={{ format: 'HH:mm' }} format="YYYY-MM-DD HH:mm" />
          </Form.Item>
          <Form.Item label="是否设置结束生效日期" name="hasEnd" rules={[{ required: true, message: '请选择' }]}>
            <Radio.Group>
              <Radio value={false}>否</Radio>
              <Radio value={true}>是</Radio>
            </Radio.Group>
          </Form.Item>

          {hasEnd && (
            <Form.Item label="开始结束时间" name="endTime" rules={[{ required: true, message: '请选择' }]}>
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format="YYYY-MM-DD HH:mm"
                onChange={(value, dateString) => {}}
              />
            </Form.Item>
          )}
        </Form>
      </Spin>
    </Modal>
  );
};

export default CreateProductionConfigModal;
